<template lang="pug">
.b-base-drawer(:style="{ 'z-index': zIndex }")
  ._overlay(:style="overlayStyle" @click="close")
  ._panel(:style="panelStyle")
    ._header
      button._close(type="button" title="Закрыть" @click="close")
        Icon(name="close3" pointer)
    ._content(:class="{ noPadding }")
      slot
</template>

<script>
import scrollLock from '@/utils/scrollLock';

// Должно совпадать с длительностью transition у ._panel и ._overlay ниже:
// после закрытия шторку убирает родитель, и убрать её нужно не раньше, чем
// она уедет за край экрана
const ANIMATION_MS = 250;

export default {
  name: 'BaseDrawer',
  props: {
    // шторка выезжает слева, а не справа
    left: Boolean,
    // число трактуем как пиксели, строку отдаём в CSS как есть ('40vw')
    width: {
      type: [Number, String],
      default: 900,
    },
    zIndex: {
      type: Number,
      default: 10000,
    },
    // фон всей шторки, вместе со строкой крестика: содержимое бывает не
    // «карточкой на белом», а страницей на своём фоне — как деталка заявки
    background: {
      type: String,
      default: 'var(--color-white)',
    },
    noPadding: Boolean,
  },
  data() {
    return {
      // выехала ли шторка: поднимаем сразу после появления в DOM, чтобы
      // сработал transition, и опускаем при закрытии — на время уезда
      shown: false,
      // закрытие уже началось: второй клик по оверлею (или Esc поверх
      // крестика) не должен заводить ещё один таймер
      closing: false,
    };
  },
  mounted() {
    scrollLock.lock();
    document.addEventListener('keyup', this.onKeyUp);

    // Заставляем браузер посчитать раскладку прямо сейчас: стартовое положение
    // должно быть зафиксировано до смены transform, иначе оба состояния
    // схлопнутся в одно и перехода не будет. Через requestAnimationFrame это
    // не делаем: в свёрнутой или перекрытой вкладке кадры не идут вовсе, и
    // шторка так и осталась бы стоять за краем экрана
    this.$el.getBoundingClientRect();
    this.shown = true;
  },
  beforeDestroy() {
    scrollLock.unlock();
    document.removeEventListener('keyup', this.onKeyUp);
    clearTimeout(this.closeTimer);
  },
  computed: {
    panelStyle() {
      return {
        width: typeof this.width === 'number' ? `${this.width}px` : this.width,
        background: this.background,
        // сторона и направление уезда — одно и то же: шторка прячется за тот
        // край, к которому прижата
        [this.left ? 'left' : 'right']: 0,
        transform: this.shown ? 'translateX(0)' : `translateX(${this.left ? '-100%' : '100%'})`,
      };
    },
    overlayStyle() {
      return { opacity: this.shown ? 1 : 0 };
    },
  },
  methods: {
    close() {
      if (this.closing) return;

      this.closing = true;
      this.shown = false;
      this.closeTimer = setTimeout(() => this.$emit('close'), ANIMATION_MS);
    },
    onKeyUp(e) {
      if (e.key === 'Escape') this.close();
    },
  },
};
</script>

<style scoped lang="scss">
.base-drawer {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  // Пока шторка выезжает, она стоит за краем экрана и высовывалась бы за него:
  // страница получала бы горизонтальный скроллбар на время анимации
  overflow: hidden;

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    cursor: pointer;
    transition: opacity 0.25s ease;
  }

  &__panel {
    position: absolute;
    top: 0;
    bottom: 0;
    // ширину задаёт panelStyle; на узком экране шторка занимает его целиком
    max-width: 100vw;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    // цвет фона задаёт panelStyle
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
    transition: transform 0.25s ease;
  }

  // Крестик в собственной строке, а не поверх содержимого: ._content ниже
  // прокручивается, и плавающая кнопка перекрывала бы уехавший под неё текст
  &__header {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    padding: 16px 16px 0;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  // Единственное место со скроллом, пока шторка открыта: страница под ней
  // заблокирована (см. scrollLock)
  &__content {
    flex-grow: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 8px 32px 32px;
    scrollbar-width: thin; // Firefox

    &.noPadding {
      padding: 0;
    }

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: none;
    }

    &::-webkit-scrollbar-button {
      display: none;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--color-purple);
    }
  }

  @media (max-width: 768px) {
    &__content {
      padding: 8px 16px 24px;
    }
  }
}
</style>
